// lib/overlay/z-scale.ts
//
// Single z-index scale for the two-slot overlay FSM (lib/overlay/overlay-state.ts:
// OverlayState = { panel, utility }). Replaces the 7x hardcoded `z-1001` sliders (profile-tab-slider,
// feed-discover-category-slider, search-mobile-shell,
// followers-following-modal, feed-search-tab-slider,
// profile-collection-slider, ChatTabSlider) — all 7 migrated as of step 1.8
// (§E, Phase 1 close-out). Every consumer of this z-tier MUST import from
// here instead of hardcoding a z-index — fitness gate #3 in Phase 1.8 (§H.3),
// frozen list empty.
//
// Step 1.6b (§C.0) reclassified TWO entries out of the FSM's own inventory —
// search is a surface, not an overlay, so `FeedSearchTabSlider`/
// `search-mobile-shell.tsx` are NOT part of the FSM's inclusion criterion
// (§C.1: shell-level mutual exclusion / z-stacking above a surface).
// `feed-search-tab-slider.tsx` was deleted outright (not migrated).
//
// `search-mobile-shell.tsx` (revised in step 1.8): 1.6b left its `z-1001`
// Tailwind class as a deliberately un-migrated literal, reasoning that
// importing `Z.overlayPanel` would misrepresent it as FSM-owned. Step 1.8
// revisits that call — `Z` is read here as TWO separable things: (a) a
// membership marker for "this kind participates in `OverlayState`" and (b) a
// plain shared numeric-constant registry for "this DOM layer sits at this
// z-tier," which any component may reference to avoid a magic number,
// independent of FSM membership (`Z.overlayBackdrop`/`overlayPanel`/
// `overlayNested` document the WHOLE z-index landscape around this tier
// below, not just the FSM's own slots — e.g. the `z-40`/`z-50`/`z-55`
// neighbours in the comment above `Z` were never FSM members either).
// `search-mobile-shell.tsx` now imports `Z.overlayPanel` for the numeric
// value ONLY — it still owns its own visibility state (the /search route's
// mount, not a `panel`/`utility` slot) and is untouched by `reduceOverlay`.
// Numerically a no-op (`Z.overlayPanel === 1001`, unchanged from the literal
// it replaces); the only change is one canonical source for the number.
// `followers-following-modal.tsx` and `profile-collection-slider.tsx` are
// the same case — real overlays with their own local `open` state (not
// migrated onto `OverlayContext`, out of scope per the spec's own §E table),
// swapped onto this scale for the same "shared numeric constant" reason.
//
// @see .tondocs/feed/OVERLAY_FSM_SPEC.md §C.3, §H.3

/**
 * Values chosen against the z-index landscape verified in the codebase on
 * 2026-07-23 (grep `z-\[`, `z-1001` under components/) so migrating an
 * existing overlay onto this scale is a visual no-op, and this tier still
 * sits correctly relative to everything NOT part of this migration:
 *
 *   z-40   sticky feed headers (mobile-product-page.tsx)
 *   z-50   base shadcn Sheet primitive (components/ui/sheet.tsx)
 *   (was z-55/60 — cart's own desktop backdrop/panel hardcode, migrated to
 *    `overlayNested` in step 1.3; mobile still rides `BottomSheet`'s shared
 *    `SHEET_Z_DEFAULT` (1400, lib/sheet-styles.ts), left untouched — that's
 *    a shared-component default used by 14+ other BottomSheet consumers,
 *    not a cart-owned hardcode, so overriding it was out of this step's
 *    scope)
 *   z-1001 the 7x legacy slider hardcodes this scale replaces
 *   z-9999 global toast layer (components/ui/toast.tsx)
 *   z-10040..10200 legacy Dialog/AlertDialog stack
 *     (components/ui/dialog.tsx, components/ui/alert-dialog.tsx,
 *     components/chat/ChatMessageOverlay.tsx)
 */
export const Z = {
  /**
   * Dim/scrim behind an overlay's own panel. One step below `overlayPanel`
   * so the two always paint in the right order; sits above all normal
   * in-flow page chrome (z-40 sticky headers, z-50 base Sheet, z-55
   * pre-migration cart backdrop) so a scrim reads as "in front of the page"
   * for every non-migrated overlay it sits next to during the incremental
   * Phase-1 rollout.
   */
  overlayBackdrop: 1000,
  /**
   * The overlay's own panel/sheet/drawer surface. Numerically matches the
   * 7x legacy `z-1001` slider hardcodes it replaces, so migrating a slider
   * onto this scale is a visual no-op.
   */
  overlayPanel: 1001,
  /**
   * Content that must render above an already-open panel. This is where the
   * `utility` slot (cart, auction-bid — lib/overlay/overlay-state.ts) paints
   * (§C.1/§C.3 two-slot amendment: "utility renders ABOVE panel"), and also
   * covers in-panel nested content (a dropdown, a confirmation strip inside
   * a sheet). Stays well below the legacy Dialog/AlertDialog stack
   * (z-10040..10200) so a Dialog opened FROM an overlay (e.g. an auth
   * prompt during checkout) still renders on top of it.
   */
  overlayNested: 1010,
  /**
   * Global toast/notification layer. Must outrank any single open overlay.
   * Matches the existing toast z-index (components/ui/toast.tsx) — this
   * tier documents that invariant, it does not move toast.
   */
  toast: 9999,
} as const;

export type ZScale = typeof Z;
